import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { businessServices, familyServices } from "../data/services";

const NAV_LINKS = [
  { label: "Where we work", to: "/where-we-work" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/#about" },
];

const DropdownItem = ({ s, close }) => (
  <Link
    to={`/services/${s.slug}`}
    onClick={close}
    data-testid={`dropdown-link-${s.slug}`}
    className="flex items-start gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-base"
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: s.soft }}>
      <s.icon className="h-5 w-5" style={{ color: s.color }} aria-hidden="true" />
    </span>
    <span>
      <span className="block text-sm font-semibold text-ink">{s.name}</span>
      <span className="mt-0.5 block text-xs leading-relaxed text-mute">{s.pitch}</span>
    </span>
  </Link>
);

export default function Header() {
  const [dropOpen, setDropOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();
  const onServices = location.pathname.startsWith("/services");

  useEffect(() => {
    if (!dropOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setDropOpen(false);
    const onDown = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [dropOpen]);

  useEffect(() => {
    setDropOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (menuOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  const linkClass = ({ isActive }) =>
    `rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 hover:bg-base ${
      isActive ? "text-ink" : "text-mute hover:text-ink"
    }`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-line bg-surface">
      <div className="mx-auto flex h-[72px] w-full max-w-[1200px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-wordmark" aria-label="Amplified Minds — home">
          <Logo />
        </Link>

        <div className="flex items-center gap-2">
          <nav className="hidden items-center gap-1 min-[900px]:flex" aria-label="Primary">
            <div
              ref={dropRef}
              className="relative"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                type="button"
                data-testid="services-dropdown-trigger"
                aria-expanded={dropOpen}
                aria-haspopup="true"
                onClick={() => setDropOpen((o) => !o)}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 hover:bg-base ${
                  onServices ? "text-ink" : "text-mute hover:text-ink"
                }`}
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>

              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3"
                  >
                    <div className="w-[min(560px,92vw)] rounded-2xl border border-line bg-surface p-5 shadow-panel" data-testid="services-dropdown">
                      <p className="am-eyebrow text-mute">For businesses</p>
                      <div className="mt-2 grid gap-1 sm:grid-cols-2">
                        {businessServices.map((s) => (
                          <DropdownItem key={s.slug} s={s} close={() => setDropOpen(false)} />
                        ))}
                      </div>
                      <p className="am-eyebrow mt-5 text-mute">For families</p>
                      <div className="mt-2 grid">
                        {familyServices.map((s) => (
                          <DropdownItem key={s.slug} s={s} close={() => setDropOpen(false)} />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV_LINKS.map(({ label, to }) => (
              <NavLink key={to} to={to} data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass}>
                {label}
              </NavLink>
            ))}
          </nav>

          <Link to="/contact" data-testid="nav-cta-contact" className="am-btn px-5 py-2.5 text-sm max-[899px]:hidden">
            Talk to us
          </Link>

          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink min-[900px]:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-50 flex h-full flex-col bg-surface"
          >
            <div className="flex h-[72px] items-center justify-between border-b border-line px-4 sm:px-6">
              <Logo />
              <button
                type="button"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-8 py-8" aria-label="Mobile">
              <p className="am-eyebrow text-mute">Services</p>
              {[...businessServices, ...familyServices].map((s, i) => (
                <motion.div key={s.slug} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`mobile-nav-link-${s.slug}`}
                    className="flex items-center gap-3 border-b border-line py-4 font-display text-xl font-bold text-ink"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: s.soft }}>
                      <s.icon className="h-4 w-4" style={{ color: s.color }} aria-hidden="true" />
                    </span>
                    {s.name}
                  </Link>
                </motion.div>
              ))}
              {NAV_LINKS.map(({ label, to }, i) => (
                <motion.div key={to} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.05 }}>
                  <Link
                    to={to}
                    data-testid={`mobile-nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block border-b border-line py-4 font-display text-xl font-bold text-ink"
                  >
                    {label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                <Link to="/contact" data-testid="mobile-nav-cta" className="am-btn mt-8 w-full px-7 py-3.5 text-sm">
                  Talk to us →
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
